import type { FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import { nanoid } from 'nanoid';
import type { PolygonObject } from '~/common/types';
import type { InitialViewState } from '~/components2/Map/common';
import { fitBoundsInitialViewState } from '../helpers';

/** Fit-to-bounds view over the union of the given feature collections; default US view if empty/invalid. */
export function computeDrawInitialViewState(...fcs: (FeatureCollection | undefined)[]): InitialViewState {
  return fitBoundsInitialViewState(fcs);
}

/**
 * Suggests an editable default name for a new sub-geometry: the next "Region N",
 * where N is one past the highest existing "Region <number>" name. Using the max
 * (rather than the count) keeps the suggestion unique even when an earlier region
 * was archived and left a gap. Falls back to "Region 1" when no "Region N" names
 * exist yet. The user can always overwrite it.
 */
export function nextDefaultSubGeometryName(existingNames: (string | null | undefined)[]): string {
  let maxN = 0;
  for (const name of existingNames) {
    const match = name?.match(/^Region (\d+)$/);
    if (match) maxN = Math.max(maxN, Number(match[1]));
  }
  return `Region ${maxN + 1}`;
}

/**
 * Converts a saved sub-geometry geometry into one draw-store PolygonObject per polygon,
 * matching the shape the canvas produces (see EditorSurface's DrawPolygon handler). Used to
 * seed the editor when editing an existing sub-geometry. Interior rings (holes) are preserved
 * because each polygon keeps its full `coordinates` array.
 */
export function geometryToPolygonObjects(
  geometry: Polygon | MultiPolygon,
): Omit<PolygonObject, 'createdBy' | 'updatedBy'>[] {
  const polygons: Polygon['coordinates'][] =
    geometry.type === 'MultiPolygon' ? geometry.coordinates : [geometry.coordinates];
  return polygons.map((coordinates) => ({
    id: nanoid(),
    sortOrder: 'abc', // matches SubGeometryDrawStore.generateSortOrderKey()
    type: 'polygon',
    title: '',
    description: '',
    geometry: { type: 'Polygon', coordinates },
    properties: {
      color: 'orange',
      fillOpacity: 0.25,
      strokeOpacity: 1,
      strokeWidth: 4,
      strokeStyle: 'solid',
      showArea: true,
      showTitle: true,
      labelAlwaysVisible: false,
    },
  }));
}
